import React, { useEffect, useRef, useState } from "react";
import { FcGoogle } from "react-icons/fc";
import axiosInstance from "../../../api/axiosInstance";
import { enqueueSnackbar } from "notistack";
import Loader from "../../../components/icons/Loader";
import Tabs, { TabContent } from "../../../components/ui/tabs";
import { IoIosArrowBack } from "react-icons/io";
import ProfilePicture from "../../../components/icons/ProfilePicture";
import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";
import { useUser } from "../../../context/UserContext/UserProvider";
import { Browser } from "@capacitor/browser";
import { PortalTitle } from "../../Portal";

const BusinessTools = () => {
  const [loading, setLoading] = useState(true);
  // const [accounts, setAccounts] = useState([]);
  const {
    calendarAccounts: accounts,
    setCalendarAccounts: setAccounts,
    fetchCalendarAccounts,
  } = useUser();
  const [currentTab, setCurrentTab] = useState("main");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const settingRef = useRef(null);

  useEffect(() => {
    if (selectedAccount) {
      setCurrentTab("settings");
    } else {
      setCurrentTab("main");
    }
  }, [selectedAccount]);

  const fetchAccounts = async () => {
    setLoading(true);
    await fetchCalendarAccounts();
    setLoading(false);
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const authGoogle = async () => {
    try {
      const { data } = await axiosInstance.get(`/user/calendar/google/auth`);
      if (data.error) {
        return enqueueSnackbar(data.error, { variant: "error" });
      }
      const url = data.url;
      // show popup to url
      await Browser.open({ url });
    } catch (error) {
      console.log(error.response.data);
      enqueueSnackbar("something went wrong, please try again", {
        variant: "error",
      });
    }
  };

  const unsyncGoogle = (id) => async () => {
    try {
      const { data } = await axiosInstance.get(
        `/user/calendar/google/unsync/${id}`
      );
      if (data.error) {
        return enqueueSnackbar(data.message, { variant: "error" });
      }
      // Optimistically remove the account from the UI
      setAccounts((prevAccounts) => prevAccounts.filter((account) => account._id !== id));
      // Also refresh from server to ensure consistency
      await fetchAccounts();
      enqueueSnackbar("account unsynced successfully", { variant: "success" });
    } catch (error) {
      console.log(error.response.data);
      enqueueSnackbar("something went wrong, please try again", {
        variant: "error",
      });
      // Refresh accounts on error to ensure UI is in sync
      await fetchAccounts();
    }
  };

  const saveSettings = (id) => async () => {
    try {
      const name = settingRef.current.value;
      if (!name) {
        return enqueueSnackbar("Please enter a name", { variant: "error" });
      }
      const { data } = await axiosInstance.post(
        `/user/calendar/google/settings/${id}`,
        {
          name,
        }
      );
      if (data.error) {
        return enqueueSnackbar(data.message, { variant: "error" });
      }
      fetchAccounts();
      enqueueSnackbar("Settings saved successfully", { variant: "success" });
    } catch (error) {
      console.log(error.response.data);
      enqueueSnackbar("something went wrong, please try again", {
        variant: "error",
      });
    }
  };

  return (
    <div className="bg-[var(--inyc-primary)] min-h-[100vh] md:min-h-0">
      <PortalTitle>calendar</PortalTitle>
      <Tabs
        hideTabs
        tabs={["main", "settings"]}
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
      >
        <TabContent name="main">
          <div className="mx-auto w-full max-w-[420px] px-kit-20">
            <button
              type="button"
              onClick={authGoogle}
              className="calendar-sync-btn mx-auto mt-kit-20 flex w-full max-w-[15.5rem] cursor-pointer items-center justify-center rounded-kit-full border-[1px] border-[var(--inyc-outline-color)] bg-[var(--inyc-primary)] px-kit-16 py-kit-6 text-kit-sm font-['Primary-Medium'] text-[var(--inyc-primary-text-color)] transition-colors duration-200 hover:bg-[var(--inyc-input-background-color)]"
            >
              <FcGoogle size={20} />
              <span className="ml-kit-10 font-['Primary-Bold']">sync google calendar</span>
            </button>
            <p className="mx-auto mt-kit-12 max-w-[22rem] text-center text-kit-sm leading-[1.45] font-['Primary-Regular'] text-[var(--inyc-primary-text-color)]">
              when you sync your calendar with in—yc, the events you post will automatically sync to your calendar.
            </p>

            <div className="portal-divider-line mt-kit-20 border-t-[1px] border-[var(--inyc-input-border-color)]" />

            <h2 className="mt-kit-20 m-0 font-['Primary-Medium'] text-kit-base leading-none text-[var(--inyc-primary-text-color)]">
              connected accounts
            </h2>

            {loading ? (
              <div className="mt-kit-16 flex justify-center">
                <Loader />
              </div>
            ) : accounts?.length > 0 ? (
              <div className="mt-kit-16 flex flex-col gap-kit-12">
                {accounts.map((account) => (
                  <article
                    key={account._id}
                    className="flex items-center justify-between gap-kit-12 rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] [:is(body.dark)_&]:bg-[var(--inyc-card-bg)] px-kit-12 py-kit-12"
                  >
                    <div className="flex min-w-0 items-center gap-kit-12">
                      <div className="flex h-[2.35rem] w-[2.35rem] shrink-0 items-center justify-center rounded-kit-full bg-[var(--inyc-input-background-color)]">
                        <FcGoogle size={24} />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate font-['Primary-Bold'] text-kit-base leading-[1.15] text-[var(--inyc-primary-text-color)]">
                          {account.meta.name}
                        </div>
                        <div className="mt-kit-4 truncate font-['Primary-Regular'] text-kit-xs leading-none text-[var(--inyc-primary-text-color-light)]">
                          {account.meta.email}
                        </div>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-kit-6">
                      <span className="inline-flex h-[21px] w-[84px] items-center justify-center rounded-kit-full border-[1px] border-success-line bg-success-soft text-kit-2xs font-['Primary-Bold'] leading-none text-success">
                        connected
                      </span>
                      <button
                        type="button"
                        onClick={unsyncGoogle(account._id)}
                        className="inline-flex h-[21px] w-[84px] items-center justify-center rounded-kit-full bg-white text-kit-2xs font-['Primary-Bold'] leading-none text-danger border-[1px] border-danger transition-colors duration-200 hover:bg-danger-soft"
                      >
                        unsync
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="mt-kit-16 text-center text-kit-sm font-['Primary-Regular'] text-[var(--inyc-primary-text-color-light)]">
                no connected accounts
              </div>
            )}
          </div>
        </TabContent>
        <TabContent className="p-kit-16" name="settings">
          <div>
            <h1
              onClick={() => setSelectedAccount(null)}
              className="flex items-center cursor-pointer font-['Primary-Bold'] text-kit-2xl"
            >
              <IoIosArrowBack size={38} className="mr-kit-6" />
              settings
            </h1>
          </div>
          <div className="p-kit-16">
            {selectedAccount && (
              <div>
                <div className="mb-kit-64 flex items-center justify-start gap-kit-16">
                  <ProfilePicture
                    style={{ width: "100px", height: "100px" }}
                    profilePicture={selectedAccount.meta.picture}
                  />
                  <div>
                    <h1 className="font-['Primary-Medium']">
                      {selectedAccount.meta.name}
                    </h1>
                    <h1 className="font-['Primary-Regular'] text-black/60">
                      {selectedAccount.meta.email}
                    </h1>
                  </div>
                </div>
                <div className="!text-kit-sm mb-kit-16">
                  <Input
                    defaultValue={selectedAccount.settings?.name}
                    Ref={settingRef}
                    label="setting"
                    placeholder="setting"
                  />
                </div>
                <div>
                  <Button onClick={saveSettings(selectedAccount._id)}>
                    Save
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabContent>
      </Tabs>
    </div>
  );
};

export default BusinessTools;
